const STORAGE_PREFIX = "adatis.catalogCategoryOnboarding";

export interface CatalogCategoryOnboardingPreferences {
  bannerDismissed: boolean;
  wizardCompleted: boolean;
  autoPromptShown: boolean;
}

const defaultPreferences = (): CatalogCategoryOnboardingPreferences => ({
  bannerDismissed: false,
  wizardCompleted: false,
  autoPromptShown: false,
});

const storageKey = (tenantId: number): string => `${STORAGE_PREFIX}.${tenantId}`;

const readPreferences = (tenantId: number): CatalogCategoryOnboardingPreferences => {
  if (typeof window === "undefined") {
    return defaultPreferences();
  }

  try {
    const raw = localStorage.getItem(storageKey(tenantId));

    if (!raw) {
      return defaultPreferences();
    }

    const parsed = JSON.parse(raw) as Partial<CatalogCategoryOnboardingPreferences>;

    return {
      bannerDismissed: Boolean(parsed.bannerDismissed),
      wizardCompleted: Boolean(parsed.wizardCompleted),
      autoPromptShown: Boolean(parsed.autoPromptShown),
    };
  } catch {
    return defaultPreferences();
  }
};

const writePreferences = (
  tenantId: number,
  patch: Partial<CatalogCategoryOnboardingPreferences>
): CatalogCategoryOnboardingPreferences => {
  const next = {
    ...readPreferences(tenantId),
    ...patch,
  };

  if (typeof window !== "undefined") {
    localStorage.setItem(storageKey(tenantId), JSON.stringify(next));
  }

  return next;
};

export const shouldShowCatalogCategoryBanner = (
  tenantId: number | null | undefined,
  useCatalogCategories: boolean
): boolean => {
  if (useCatalogCategories || tenantId == null) {
    return false;
  }

  const preferences = readPreferences(tenantId);

  return !preferences.bannerDismissed && !preferences.wizardCompleted;
};

export const shouldAutoOpenCatalogCategoryWizard = (
  tenantId: number | null | undefined,
  useCatalogCategories: boolean,
  canConfigure: boolean
): boolean => {
  if (!canConfigure || useCatalogCategories || tenantId == null) {
    return false;
  }

  const preferences = readPreferences(tenantId);

  return (
    !preferences.autoPromptShown &&
    !preferences.bannerDismissed &&
    !preferences.wizardCompleted
  );
};

export const markCatalogCategoryBannerDismissed = (tenantId: number): void => {
  writePreferences(tenantId, { bannerDismissed: true });
};

export const markCatalogCategoryAutoPromptShown = (tenantId: number): void => {
  writePreferences(tenantId, { autoPromptShown: true });
};

export const markCatalogCategoryWizardCompleted = (tenantId: number): void => {
  writePreferences(tenantId, {
    wizardCompleted: true,
    bannerDismissed: true,
  });
};

export const resetCatalogCategoryOnboardingPreferences = (
  tenantId: number
): CatalogCategoryOnboardingPreferences =>
  writePreferences(tenantId, defaultPreferences());
